function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Constants } from '@shared/utils/constants';
import { createSetCurrentViewAction } from '@shared/actions/actions';
import { loadTileData } from '@shared/actions/tileDataThunkActions';
import { useLocationChange } from '@shared/hooks/useLocationChange';
import { useClientRoutes } from '@shared/hooks/useClientRoutes';
import { PATHS_TO_VIEW_MAP } from './pathsViewMap';
import { routes as routesHistory } from '@shared/routerHistory';
import AppView from '@shared/containers/AppView';
const AuthRedirect = /*#__PURE__*/ React.lazy(()=>import('@shared/components/AuthRedirect'));
const Checkout = /*#__PURE__*/ React.lazy(()=>import('./containers/LegacyCheckoutHydration'));
const SitemapContainer = /*#__PURE__*/ React.lazy(()=>import('./containers/sitemap'));
const PartnersHostContainer = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/partnersHost'));
const MyReviews = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/myReview'));
const DynamicCampaign = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/dynamicCampaign'));
const UserFavouriteDetail = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/userFavouriteDetail'));
const UserPrivateOffersGallery = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/userPrivateOffersGallery'));
const Home = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "home" */ '@shared/containers/home'));
const AppDetails = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "appDetails" */ '@appsource/containers/appDetails'));
const ServiceDetails = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "serviceDetails" */ '@appsource/containers/serviceDetails'));
const CommonPageDetails = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "commonPageDetails" */ '@appsource/components/commonPageDetails'));
const GalleryPage = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "galleryPage" */ '@shared/containers/galleryPage'));
/**
 * Where actual routes should be declared, because the object is also used in the SSR to identify matched routes
 */ export const getRoutes = ()=>[
        {
            path: Constants.locale,
            element: /*#__PURE__*/ _jsx(AppView, {}),
            children: [
                {
                    index: true,
                    element: /*#__PURE__*/ _jsx(Home, {})
                },
                {
                    path: Constants.home,
                    element: /*#__PURE__*/ _jsx(Home, {}),
                    redirectTo: '/'
                },
                {
                    path: 'marketplace',
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.appsGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {}),
                    redirectTo: Constants.newAppsGallery
                },
                {
                    path: Constants.newAppsGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.cloudsIndustryGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {}),
                    redirectTo: Constants.newCloudsIndustryGallery
                },
                {
                    path: Constants.newCloudsIndustryGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.productWithCompany,
                    element: /*#__PURE__*/ _jsx(AppDetails, {}),
                    redirectTo: Constants.productRoute
                },
                {
                    path: Constants.marketplaceCheckout,
                    element: /*#__PURE__*/ _jsx(Checkout, {}),
                    redirectTo: Constants.checkout
                },
                {
                    path: Constants.checkout,
                    element: /*#__PURE__*/ _jsx(Checkout, {})
                },
                {
                    path: Constants.productRoute,
                    element: /*#__PURE__*/ _jsx(CommonPageDetails, {})
                },
                {
                    path: 'user/my-reviews',
                    element: /*#__PURE__*/ _jsx(MyReviews, {})
                },
                {
                    path: Constants.csGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {}),
                    redirectTo: Constants.newCsGallery
                },
                {
                    path: Constants.newCsGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.consultingServicesOldProduct,
                    element: /*#__PURE__*/ _jsx(ServiceDetails, {}),
                    redirectTo: Constants.productRoute
                },
                {
                    path: `${Constants.partnersAppSource}/*`,
                    element: /*#__PURE__*/ _jsx(PartnersHostContainer, {}),
                    redirectTo: `${Constants.newPartnersAppSource}/*`
                },
                {
                    path: `${Constants.newPartnersAppSource}/*`,
                    element: /*#__PURE__*/ _jsx(PartnersHostContainer, {})
                },
                {
                    path: Constants.consultingServicesLegacyProduct,
                    element: /*#__PURE__*/ _jsx(ServiceDetails, {}),
                    redirectTo: Constants.productRoute
                },
                {
                    path: Constants.campaignDetails,
                    element: /*#__PURE__*/ _jsx(DynamicCampaign, {})
                },
                {
                    path: `user/my-favourites`,
                    element: /*#__PURE__*/ _jsx(UserFavouriteDetail, {})
                },
                {
                    path: `user/private-offers`,
                    element: /*#__PURE__*/ _jsx(UserPrivateOffersGallery, {})
                },
                {
                    path: Constants.privateProductWithCompany,
                    element: /*#__PURE__*/ _jsx(AppDetails, {}),
                    redirectTo: Constants.privateProductRoute
                },
                {
                    path: Constants.privateProductRoute,
                    element: /*#__PURE__*/ _jsx(AppDetails, {})
                },
                {
                    path: 'sitemap',
                    element: /*#__PURE__*/ _jsx(SitemapContainer, {})
                }
            ]
        },
        {
            path: 'redirect',
            element: /*#__PURE__*/ _jsx(AuthRedirect, {})
        }
    ];
export const getView = (routeMatch)=>routeMatch.route.index ? PATHS_TO_VIEW_MAP.home : PATHS_TO_VIEW_MAP[routeMatch.route.path];
const getCurrentView = ({ state, view, matchedRoute })=>{
    if (view !== routesHistory.commonPageDetails.name) {
        return view;
    }
    const entityId = matchedRoute.params.entityId;
    if (entityId in state.cloudsIndustry.idMap) {
        return 'cloudIndustryDetails';
    }
    if (entityId in state.services.idMap) {
        return routesHistory.serviceDetail.name;
    }
    return routesHistory.appDetails.name;
};
const Router = ()=>{
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const state = useSelector((state)=>state);
    const setCurrentView = useCallback(function(matchedRoute, featureFlag = '') {
        return _async_to_generator(function*() {
            var _getView;
            const view = (_getView = getView(matchedRoute)) === null || _getView === void 0 ? void 0 : _getView.view;
            if (!view) {
                return;
            }
            const query = new URLSearchParams(location.search);
            if (featureFlag && query.get(featureFlag) === 'true') {
                // the route is under a feature flag but the query param didn't have the feature flag set
                // treat is as a route never existed
                navigate('/404');
            }
            dispatch(createSetCurrentViewAction({
                currentView: getCurrentView({
                    state,
                    view,
                    matchedRoute
                })
            }));
            // @ts-expect-error known dispatch type error
            dispatch(loadTileData());
        })();
    }, [
        dispatch,
        location,
        navigate
    ]);
    const clientRoutes = useMemo(()=>getRoutes(), []);
    const routes = useClientRoutes({
        routes: clientRoutes
    });
    useLocationChange({
        setCurrentView,
        routes
    });
    return useRoutes(routes);
};
export default Router;
